import React from "react";
import {
  AudioOutlined,
  EnvironmentOutlined,
  FileOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { PRIMARY } from "../../CustomUtils.js/utils";
import { isLocationMessage } from "../locationUtils";

const MEDIA_TYPES = new Set(["image", "video", "audio", "document", "sticker"]);

const ReplyTypeIcon = ({ type }) => {
  switch (String(type || "").toLowerCase()) {
    case "image":
    case "sticker":
      return <PictureOutlined style={{ fontSize: 12, marginRight: 4 }} />;
    case "video":
      return <VideoCameraOutlined style={{ fontSize: 12, marginRight: 4 }} />;
    case "audio":
      return <AudioOutlined style={{ fontSize: 12, marginRight: 4 }} />;
    case "document":
      return <FileOutlined style={{ fontSize: 12, marginRight: 4 }} />;
    case "location":
      return <EnvironmentOutlined style={{ fontSize: 12, marginRight: 4 }} />;
    default:
      return null;
  }
};

const resolveQuotedMessageType = (replyTo) => {
  const type = String(replyTo?.type || replyTo?.msgType || "text").toLowerCase();
  if (type === "location" || isLocationMessage(replyTo)) {
    return "location";
  }
  return type;
};

const getCircleIcon = (type, text) => {
  const typeStr = String(type || "").toLowerCase();
  const textStr = String(text || "").toLowerCase();

  switch (typeStr) {
    case "location":
      return <EnvironmentOutlined style={{ fontSize: 16 }} />;
    case "contact":
    case "vcard":
      return <UserOutlined style={{ fontSize: 16 }} />;
    case "document":
      return <FileOutlined style={{ fontSize: 16 }} />;
    case "audio":
      return <AudioOutlined style={{ fontSize: 16 }} />;
    case "image":
    case "sticker":
      return <PictureOutlined style={{ fontSize: 16 }} />;
    case "video":
      return <VideoCameraOutlined style={{ fontSize: 16 }} />;
    default:
      if (textStr.includes("location")) {
        return <EnvironmentOutlined style={{ fontSize: 16 }} />;
      }
      return null;
  }
};

const QuotedMessagePreview = ({
  replyTo,
  contactName = "Contact",
  isOutgoingBubble = false,
  onJumpToMessage,
}) => {
  if (!replyTo?.text) {
    return null;
  }

  const isQuotedFromMe = replyTo.from === "me";
  const accentColor = isQuotedFromMe ? PRIMARY : "#53bdeb";
  const senderLabel = isQuotedFromMe ? "You" : contactName;
  const quotedType = resolveQuotedMessageType(replyTo);
  const showMediaIcon = MEDIA_TYPES.has(quotedType);
  const circleIcon = getCircleIcon(quotedType, replyTo.text);

  const handleClick = () => {
    if (typeof onJumpToMessage === "function" && replyTo?.id) {
      onJumpToMessage(replyTo.id);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      title={replyTo.id ? "Go to quoted message" : undefined}
      style={{
        display: "block",
        width: "100%",
        marginBottom: 6,
        padding: "6px 12px",
        border: "none",
        borderLeft: `4px solid ${accentColor}`,
        borderRadius: 6,
        overflow: "hidden",
        background: isOutgoingBubble ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.04)",
        cursor: replyTo.id ? "pointer" : "default",
        textAlign: "left",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          minHeight: 32,
        }}
      >
        {circleIcon && (
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              backgroundColor: accentColor + "1f",
              color: accentColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: 8,
              flexShrink: 0,
            }}
          >
            {circleIcon}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              color: accentColor,
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.3,
              marginBottom: 2,
            }}
          >
            {senderLabel}
          </div>
          <div
            style={{
              color: "#667781",
              fontSize: 13,
              lineHeight: 1.35,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "flex",
              alignItems: "center",
            }}
          >
            {!circleIcon && showMediaIcon ? <ReplyTypeIcon type={quotedType} /> : null}
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {replyTo.text}
            </span>
          </div>
        </div>
      </div>
    </button>
  );
};

export default QuotedMessagePreview;
